
import { createContext, useContext, useEffect, useState } from "react";
import { User, UserRole } from "../types";
import { useToast } from "@/hooks/use-toast"; 
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Storage key used consistently across all environments
const USER_SESSION_KEY = 'taibah_user_session';

function isValidUserRole(role: string): role is UserRole {
  return role === "SUPER_ADMIN" || role === "NURSE";
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleAuthError = async (error: any) => {
    console.error('Auth error:', error);
    
    toast({
      title: "Authentication Error",
      description: error.message || "An error occurred",
      variant: "destructive",
    });
  };

  const fetchUserProfile = async (email: string) => {
    try {
      console.log('Fetching profile for user:', email);
      const { data: profile, error } = await supabase
        .from("profiles")
        .select(`
          id,
          name,
          email,
          role,
          school_id,
          profile_picture,
          display_name,
          user_id
        `)
        .eq('email', email)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        throw error;
      }

      if (!profile) {
        console.error('No profile found for user:', email);
        toast({
          title: "Error",
          description: "No profile found for user",
          variant: "destructive",
        });
        return null;
      }

      if (!isValidUserRole(profile.role)) {
        console.error('Invalid role:', profile.role);
        toast({
          title: "Error",
          description: "Invalid user role",
          variant: "destructive",
        });
        return null;
      }

      // Fetch school name if school_id exists
      let schoolName = null;
      if (profile.school_id) {
        try {
          console.log('Fetching school name for school ID:', profile.school_id);
          
          // Log raw response for debugging
          const rawSchoolResponse = await supabase
            .from('schools')
            .select('*')
            .eq('id', profile.school_id);
            
          console.log('Raw school response:', rawSchoolResponse);
          
          const { data: school, error: schoolError } = await supabase
            .from('schools')
            .select('name')
            .eq('id', profile.school_id)
            .maybeSingle();
          
          if (schoolError) {
            console.error('Error fetching school:', schoolError);
            
            // Try again with a simpler query that should work with the public policy
            console.log('Trying alternate fetch with public policy...');
            const { data: publicSchool } = await supabase
              .from('schools')
              .select('name')
              .eq('id', profile.school_id)
              .maybeSingle();
              
            if (publicSchool) {
              schoolName = publicSchool.name;
              console.log('School name found via public policy:', schoolName);
            }
          } else if (school) {
            schoolName = school.name;
            console.log('School name found via normal query:', schoolName);
          } else {
            console.log('No school found for ID:', profile.school_id);
          }
        } catch (fetchError) {
          console.error('Error in school name fetch:', fetchError);
        }
      } else {
        console.log('No school_id in profile');
      }

      const userData = {
        id: profile.user_id || profile.id, // Use user_id if available, otherwise use profile id
        name: profile.display_name || profile.name,
        email: profile.email,
        role: profile.role,
        schoolId: profile.school_id,
        schoolName: schoolName,
        profilePicture: profile.profile_picture,
        profileId: profile.id, // Always include profileId for direct access to the profile table ID
      };

      console.log('User profile fetched successfully:', userData);
      return userData;
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
      throw error;
    }
  };

  useEffect(() => {
    let mounted = true;

    // Check for existing session in localStorage (custom auth)
    (async () => {
      try {
        const savedSession = localStorage.getItem(USER_SESSION_KEY);
        if (savedSession && mounted) {
          try {
            const parsedSession = JSON.parse(savedSession);
            const profile = await fetchUserProfile(parsedSession.email);
            if (profile) setUser(profile);
          } catch (e) {
            console.warn('Session parse error, clearing:', e);
            localStorage.removeItem(USER_SESSION_KEY);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) handleAuthError(error);
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      console.log('Attempting custom login for:', email);

      // Custom authentication - check profiles table directly
      const { data: profile, error } = await supabase
        .from("profiles")
        .select(`
          id,
          name,
          email,
          role,
          school_id,
          password,
          profile_picture,
          display_name,
          user_id
        `)
        .eq('email', email)
        .single();

      if (error || !profile) {
        throw new Error('Invalid email or password');
      }

      // Check password match (plain text comparison - update to bcrypt in production!)
      if (profile.password !== password) {
        throw new Error('Invalid email or password');
      }

      if (!isValidUserRole(profile.role)) {
        throw new Error('Invalid user role');
      }

      // Fetch school name if school_id exists
      let schoolName = null;
      if (profile.school_id) {
        const { data: school } = await supabase
          .from('schools')
          .select('name')
          .eq('id', profile.school_id)
          .maybeSingle();
        schoolName = school?.name || null;
      }

      const userData = {
        id: profile.user_id || profile.id,
        name: profile.display_name || profile.name,
        email: profile.email,
        role: profile.role,
        schoolId: profile.school_id,
        schoolName: schoolName,
        profilePicture: profile.profile_picture,
        profileId: profile.id,
      };

      setUser(userData);

      // Store session in localStorage
      localStorage.setItem(USER_SESSION_KEY, JSON.stringify({
        email,
        timestamp: new Date().toISOString(),
      }));

      // Welcome toast
      toast({
        title: userData.schoolName ? `Welcome to ${userData.schoolName}` : 'Welcome',
        description: `You are logged in as ${userData.name}`,
      });

      // Redirect by role
      if (userData.role === 'NURSE') navigate('/nurse-dashboard');
      else navigate('/user-management');
    } catch (error: any) {
      console.error('Login error:', error);
      const msg = error?.message || 'Login failed. Please try again.';
      handleAuthError({ message: msg });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log('Logging out...');
      setUser(null);
      queryClient.clear();
      localStorage.removeItem(USER_SESSION_KEY);
      navigate('/login');
      toast({ title: 'Success', description: 'Logged out successfully' });
    } catch (error) {
      console.error('Logout error:', error);
      handleAuthError(error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
